/**
 * AuthenticationGuard Component
 *
 * Protects application routes by ensuring users are authenticated before accessing content.
 * Handles loading states, error conditions, and redirects to appropriate authentication flows.
 * Works with MSAL and the AuthContext to provide seamless authentication experience.
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Center,
  Loader,
  Stack,
  Text,
  Alert,
  Button,
  Box,
  Progress
} from '@mantine/core';
import { IconAlertCircle, IconRefresh, IconLock } from '@tabler/icons-react';
import { useAuth } from '../shared/contexts/AuthContext';
import { isPublicRoute } from '../shared/config/authConfig';
import LoginPage from './LoginPage';

/**
 * Loading screen component for authentication states
 */
const AuthLoadingScreen = ({ message = "Verifying authentication..." }) => {
  return (
    <Container size="sm" py={80}>
      <Center>
        <Stack align="center" gap="lg">
          <Box pos="relative">
            <Loader size="xl" variant="bars" />
            <Progress
              value={100}
              animated
              size="xs"
              mt="md"
              w={200}
              color="blue"
            />
          </Box>
          <Stack align="center" gap="xs">
            <Text size="lg" fw={500}>
              {message}
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Please wait while we secure your session...
            </Text>
          </Stack>
        </Stack>
      </Center>
    </Container>
  );
};

/**
 * Error screen component for authentication failures
 */
const AuthErrorScreen = ({ error, onRetry, onLogin }) => {
  return (
    <Container size="sm" py={80}>
      <Center>
        <Stack align="center" gap="lg" maw={400}>
          <Alert
            icon={<IconAlertCircle size={24} />}
            title="Authentication Error"
            color="red"
            variant="light"
            style={{ width: '100%' }}
          >
            <Stack gap="sm">
              <Text size="sm">
                {error || "An error occurred during authentication. Please try again."}
              </Text>
              <Stack gap="xs">
                <Button
                  variant="light"
                  color="red"
                  size="sm"
                  leftSection={<IconRefresh size={16} />}
                  onClick={onRetry}
                  fullWidth
                >
                  Retry Authentication
                </Button>
                <Button
                  variant="filled"
                  color="blue"
                  size="sm"
                  leftSection={<IconLock size={16} />}
                  onClick={onLogin}
                  fullWidth
                >
                  Sign In Again
                </Button>
              </Stack>
            </Stack>
          </Alert>
        </Stack>
      </Center>
    </Container>
  );
};

/**
 * Protected route wrapper that requires authentication
 */
const ProtectedRoute = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    authError,
    refreshAuth,
    login,
    clearError
  } = useAuth();

  const location = useLocation();

  // Handle authentication refresh on mount and route changes
  useEffect(() => {
    if (!isAuthenticated && !isLoading && !authError) {
      refreshAuth();
    }
  }, [isAuthenticated, isLoading, authError, refreshAuth, location.pathname]);

  // Clear errors when route changes
  useEffect(() => {
    if (authError) {
      clearError();
    }
  }, [location.pathname, authError, clearError]);

  // Show loading screen while authentication is in progress
  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  // Show error screen if authentication failed
  if (authError && !isAuthenticated) {
    return (
      <AuthErrorScreen
        error={authError}
        onRetry={refreshAuth}
        onLogin={login}
      />
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Render protected content if authenticated
  return children;
};

/**
 * Main AuthenticationGuard component
 */
const AuthenticationGuard = ({ children }) => {
  const location = useLocation();

  // Check if current route is public (doesn't require authentication)
  const isCurrentRoutePublic = isPublicRoute(location.pathname);

  // Allow access to public routes without authentication
  if (isCurrentRoutePublic) {
    return children;
  }

  // Wrap protected routes with authentication requirements
  return (
    <ProtectedRoute>
      {children}
    </ProtectedRoute>
  );
};

/**
 * Higher-order component for wrapping individual components that require authentication
 */
export const withAuthenticationGuard = (Component) => {
  const WrappedComponent = (props) => {
    return (
      <AuthenticationGuard>
        <Component {...props} />
      </AuthenticationGuard>
    );
  };

  WrappedComponent.displayName = `withAuthenticationGuard(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

/**
 * Hook to check if current route requires authentication
 */
export const useRequiresAuth = () => {
  const location = useLocation();
  return !isPublicRoute(location.pathname);
};

/**
 * Component to conditionally render content based on authentication status
 */
export const AuthenticatedOnly = ({ children, fallback = null }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return fallback || <Loader size="sm" />;
  }

  return isAuthenticated ? children : fallback;
};

/**
 * Component to render content only for unauthenticated users
 */
export const UnauthenticatedOnly = ({ children, fallback = null }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return fallback || <Loader size="sm" />;
  }

  return !isAuthenticated ? children : fallback;
};

export default AuthenticationGuard;